// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";


import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";


// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";

// Moduli specifični za projekt
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

import { Dialog } from "ui/components/Dialog";

import { getFleetVehicleInvoiceRentsAPI } from "services/api/fleet/vehicles/invoices/api";


import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import InvoiceRentSearchFilters from "../InvoiceRentSearchFilters/InvoiceRentSearchFilters";
import InvoiceRentSearchResults from "../InvoiceRentSearchResults/InvoiceRentSearchResults";
import VehicleInvoiceRentEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleInvoiceRentEditCard/VehicleInvoiceRentEditCard";
import { InvoiceRentSearchPageCmp, InvoiceRentSearchPageProps } from "./types";
import { InvoiceRentRow, InvoiceOutgoingSearchFormValues } from "services/api/fleet/vehicles/invoices/types";
import { initialFleetVehicleInvoiceSearchForm } from "services/api/fleet/vehicles/invoices/consts";

const InvoiceRentSearchTab: InvoiceRentSearchPageCmp = (
    props: InvoiceRentSearchPageProps
) => {

    const { t } = useTranslate();

    const navigate = useNavigate();

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<InvoiceRentRow>
    >(initialDataWithPagination);

    const [filters, setFilters] = useState<InvoiceOutgoingSearchFormValues>(
        initialFleetVehicleInvoiceSearchForm
    );

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [invoiceId, setInvoiceId] = useState<string>("");
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);


    useEffect(() => {
        fetchSearchResult(searchResults.pagination);
    }, []);

    const fetchSearchResult = async (pagination: Pagination, filterValues?: InvoiceOutgoingSearchFormValues) => {
        setIsLoading(true);
        try {
            const f = filterValues ?? filters;
            const results = await getFleetVehicleInvoiceRentsAPI(pagination, {
                invoiceStatusId: f.invoiceStatusId ?? undefined,
                search: f.search ?? undefined,
                invoiceDateFrom: f.invoiceDateFrom,
                invoiceDateTo: f.invoiceDateTo,
                dueDateFrom: f.dueDateFrom,
                dueDateTo: f.dueDateTo,
                totalMin: f.totalMin,
                totalMax: f.totalMax,
                contactClientId: f.contactClientId ?? undefined,
            });

            setSearchResults(results);
        } catch {
            setSearchResults(initialDataWithPagination);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleRowClick = useCallback(async (id: string) => {
        setInvoiceId(id);
        setShowAddNewModal(true);
    }, []);

    const handleAddNew = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setInvoiceId("");
            setShowAddNewModal(true);
        },
        []
    );

    const handleEditSuccess = useCallback(() => {
        fetchSearchResult(searchResults.pagination);
        setInvoiceId("");
        setShowAddNewModal(false);
    }, [searchResults.pagination]);

    const handleSearchChange = useCallback((formValues: InvoiceOutgoingSearchFormValues) => {
        setFilters(formValues);
        fetchSearchResult(searchResults.pagination, formValues);
    }, [searchResults.pagination]);

    const handleSelectedChange = useSelectedItemsChanges(
        selectedRows,
        setSelectedRows
    );

    const handlePaginationChange = (newPagination: Pagination) => {
        fetchSearchResult(newPagination);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        setInvoiceId(id);
        setOpenDialog(true);
    }

    const handleInvoiceDeleteDialogConfirm = useCallback(async () => {
        if (!invoiceId) return;

        try {
            // zatvori dijalog nakon brisanja
            setOpenDialog(false);
            setInvoiceId("");
        } catch (err) {
            console.error("Greška prilikom brisanja dokumenta:", err);
        }
    }, [invoiceId, t]);

    const handleInvoiceDeleteDialogClose = useCallback(() => {
        setInvoiceId("");
        setOpenDialog(false);
    }, []);

    const handleVehicleClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                FleetVehicleRoutes.FleetVehicleDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                ContactRoutes.ContactDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    return (
        <>

            <div className="px-0 py-6 mx-[-1px] mt-[-1px] ">
                <InvoiceRentSearchFilters
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                />
                {isLoading && searchResults.data.length === 0 ? (
                    <div className="relative min-h-[200px]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
                    <NoResults message="Unesite dokument klikom na 'Dodaj račun'" />
                ) : (
                    <InvoiceRentSearchResults
                        searchResults={searchResults}
                        selectedRows={selectedRows}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPaginationChange={handlePaginationChange}
                        onSelectedChange={handleSelectedChange}
                        onVehicleClick={handleVehicleClick}
                        onContactClick={handleContactClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>

            <Dialog
                open={openDialog}
                title={t(
                    "admin.finance.invoice.dialog.delete.title"
                )}
                message={t(
                    "admin.finance.invoice.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.finance.invoice.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.finance.invoice.dialog.delete.canceltext"
                )}
                onConfirm={handleInvoiceDeleteDialogConfirm}
                onCancel={handleInvoiceDeleteDialogClose}
            />
            <VehicleInvoiceRentEditCard
                show={showAddNewModal}
                vehicleId={""}
                invoiceId={invoiceId}
                onSuccess={handleEditSuccess}
                onClose={handleEditSuccess}
            />
        </>
    );
};

InvoiceRentSearchTab.route =
    FinanceRoutes.InvoiceSearchPageRouter;

export default InvoiceRentSearchTab;
